import { useQuery } from "@tanstack/react-query";
import { ShieldAlert, ShieldCheck, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";

/**
 * 🛑 Daily Loss Limit Banner
 *
 * Kapalıyken (limit=0) hiç görünmez.
 * Aktifken küçük bir status pill: "🛑 Günlük K/Z: -$X / -$Y (%Z kullanıldı)"
 * Bloklanma anında geniş kırmızı banner: "🛑 ZARAR KESİCİ DEVREDE — yeni hedge yarın UTC 00:00'a kadar kapalı"
 */
export default function DailyLossLimitBanner() {
  const { data } = useQuery({
    queryKey: ["daily-pnl"],
    queryFn: api.dailyPnl,
    refetchInterval: 15000,
  });

  if (!data) return null;
  const { today_net = 0, limit = 0, blocked = false, used_pct = 0 } = data;

  if (!limit || limit <= 0) return null; // devre kesici kapalı

  // Renk: 0-50% yeşilimsi, 50-80% sarı, 80-100% turuncu, 100%+ kırmızı
  let color = "text-emerald-400 border-emerald-500/30 bg-emerald-500/5";
  let Icon = ShieldCheck;
  if (used_pct >= 100 || blocked) {
    color = "text-red-300 border-red-600/50 bg-red-600/10";
    Icon = ShieldAlert;
  } else if (used_pct >= 80) {
    color = "text-orange-300 border-orange-500/40 bg-orange-500/10";
    Icon = AlertTriangle;
  } else if (used_pct >= 50) {
    color = "text-yellow-300 border-yellow-500/30 bg-yellow-500/5";
    Icon = AlertTriangle;
  }

  const netStr = (today_net >= 0 ? "+" : "") + "$" + today_net.toFixed(2);
  const limitStr = "-$" + limit.toFixed(0);

  if (blocked) {
    return (
      <div
        data-testid="daily-loss-blocked-banner"
        className={`flex items-center gap-3 p-3 rounded-sm border ${color}`}
      >
        <Icon className="w-5 h-5 shrink-0" />
        <div className="flex-1 text-sm leading-tight">
          <div className="font-bold uppercase tracking-wider">
            🛑 Günlük Zarar Kesici DEVREDE
          </div>
          <div className="text-xs mt-0.5 opacity-90">
            Bugünkü net K/Z <b>{netStr}</b> · limit <b>{limitStr}</b>.
            Yeni pozisyon açılışı <b>yarın UTC 00:00&apos;a kadar kapalı</b>.
            Limiti Ayarlar &gt; Genel&apos;den değiştirebilirsin.
          </div>
        </div>
        <span className="text-xs font-mono">%{used_pct.toFixed(0)}</span>
      </div>
    );
  }

  // Compact pill (uyarı gerek yok ama kullanıcı durumu görsün)
  return (
    <div
      data-testid="daily-loss-status-pill"
      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-sm border text-xs ${color}`}
      title={`Bugünkü net K/Z: ${netStr} · Limit: ${limitStr}`}
    >
      <Icon className="w-3.5 h-3.5" />
      <span className="font-mono uppercase tracking-wider">
        Günlük K/Z <b>{netStr}</b> / {limitStr}
      </span>
      {used_pct > 0 && (
        <span className="font-mono opacity-70">· %{used_pct.toFixed(0)}</span>
      )}
    </div>
  );
}
